import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { NavLink, Navigate, Outlet, Route, Routes } from "react-router-dom";
import {
  getWorkspaceKey,
  setWorkspaceKey,
  syncDrills,
} from "./lib/cloud-drill-storage";
import { syncPracticePlan } from "./lib/cloud-practice-storage";
import {
  getCoachAuthClient,
  migrateRecoveryWorkspace,
  sendCoachMagicLink,
} from "./lib/coach-auth";
import { EditorRoute } from "./routes/EditorRoute";
import { LibraryRoute } from "./routes/LibraryRoute";
import { PracticePlannerRoute } from "./routes/PracticePlannerRoute";
import { PracticeTemplatesRoute } from "./routes/PracticeTemplatesRoute";

function Shell() {
  const [cloudReady, setCloudReady] = useState(false);
  const [cloudSynced, setCloudSynced] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [restoreKey, setRestoreKey] = useState("");
  const [session, setSession] = useState<Session | null>(null);
  const [email, setEmail] = useState("");
  const [accountMessage, setAccountMessage] = useState("");
  const workspaceKey = getWorkspaceKey();

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    const loadWorkspace = async (nextSession: Session | null) => {
      if (nextSession) await migrateRecoveryWorkspace(nextSession);
      const results = await Promise.all([syncDrills(), syncPracticePlan()]);
      if (!active) return;
      setSession(nextSession);
      setCloudSynced(results.every((result) => result.synced));
      setCloudReady(true);
    };
    void getCoachAuthClient()
      .then(async (client) => {
        const { data } = await client.auth.getSession();
        await loadWorkspace(data.session);
        const listener = client.auth.onAuthStateChange(
          (_event, nextSession) => {
            void loadWorkspace(nextSession);
          },
        );
        unsubscribe = () => listener.data.subscription.unsubscribe();
      })
      .catch(() => void loadWorkspace(null));
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const restoreWorkspace = async () => {
    if (!setWorkspaceKey(restoreKey)) return;
    setCloudReady(false);
    const results = await Promise.all([syncDrills(), syncPracticePlan()]);
    setCloudSynced(results.every((result) => result.synced));
    setCloudReady(true);
    setBackupOpen(false);
    window.location.reload();
  };

  const sendSignInLink = async () => {
    setAccountMessage("Sending secure sign-in link…");
    const { error } = await sendCoachMagicLink(email.trim());
    setAccountMessage(
      error
        ? error.message
        : "Check your email and open the Puckly Coach sign-in link.",
    );
  };

  const signOut = async () => {
    const client = await getCoachAuthClient();
    await client.auth.signOut();
    setSession(null);
    setAccountMessage("");
  };

  return (
    <div className="app-shell">
      <header className="site-header">
        <NavLink to="/drills" className="brand" aria-label="Puckly Coach home">
          <img src="/brand/puckly-logo-white.png" alt="Puckly Hockey" />
          <span>Coach</span>
        </NavLink>
        <nav aria-label="Coach navigation">
          <NavLink to="/drills">Drills</NavLink>
          <NavLink to="/practice">Practice plans</NavLink>
          <NavLink to="/practice-templates">Templates</NavLink>
        </nav>
        <button
          className={`cloud-backup-button ${cloudSynced ? "synced" : "local"}`}
          onClick={() => setBackupOpen(true)}
        >
          <span aria-hidden="true">{cloudSynced ? "●" : "○"}</span>
          {session?.user.email ??
            (cloudSynced ? "Cloud backed up" : "Local backup")}
        </button>
      </header>
      {cloudReady ? (
        <Outlet />
      ) : (
        <main className="cloud-loading" aria-live="polite">
          <span />
          Loading your drill library…
        </main>
      )}
      {backupOpen && (
        <div className="cloud-modal-backdrop" role="presentation">
          <section
            className="cloud-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cloud-backup-title"
          >
            <button
              className="cloud-modal-close"
              onClick={() => setBackupOpen(false)}
              aria-label="Close cloud backup"
            >
              ×
            </button>
            <p className="eyebrow">Puckly Coach account</p>
            <h2 id="cloud-backup-title">
              {session ? "Your coaching workspace" : "Sign in on every device"}
            </h2>
            {session ? (
              <div className="coach-account-card">
                <div>
                  <span>Signed in as</span>
                  <strong>{session.user.email}</strong>
                </div>
                <button onClick={() => void signOut()}>Sign out</button>
              </div>
            ) : (
              <div className="coach-sign-in">
                <p>
                  Enter your email and we’ll send a secure passwordless sign-in
                  link. Your current drills and practice plan will move into the
                  account automatically.
                </p>
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="coach@example.com"
                    aria-label="Coach email"
                  />
                  <button
                    disabled={!email.includes("@")}
                    onClick={() => void sendSignInLink()}
                  >
                    Email sign-in link
                  </button>
                </div>
                {accountMessage && (
                  <p className="account-message">{accountMessage}</p>
                )}
              </div>
            )}
            <details className="recovery-key-details">
              <summary>Recovery key fallback</summary>
              <p>
                Keep this key as an offline fallback or use it to restore the
                pre-account workspace.
              </p>
              <div className="recovery-key-row">
                <input
                  readOnly
                  value={workspaceKey}
                  aria-label="Recovery key"
                />
                <button
                  onClick={() =>
                    void navigator.clipboard.writeText(workspaceKey)
                  }
                >
                  Copy
                </button>
              </div>
            </details>
            {!session && (
              <div className="restore-workspace">
                <label htmlFor="restore-key">Restore an existing library</label>
                <div>
                  <input
                    id="restore-key"
                    value={restoreKey}
                    onChange={(event) => setRestoreKey(event.target.value)}
                    placeholder="Paste your 64-character recovery key"
                  />
                  <button onClick={() => void restoreWorkspace()}>
                    Restore
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Navigate to="/drills" replace />} />
        <Route path="drills" element={<LibraryRoute />} />
        <Route path="drills/new" element={<EditorRoute />} />
        <Route path="drills/:drillId/edit" element={<EditorRoute />} />
        <Route path="practice" element={<PracticePlannerRoute />} />
        <Route path="practice-templates" element={<PracticeTemplatesRoute />} />
        <Route path="*" element={<Navigate to="/drills" replace />} />
      </Route>
    </Routes>
  );
}
